import { useAuth } from 'auth';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { getAppServices } from 'providers/app-services';
import { FeatureFlag } from 'featureFlags';
import { useTranslator } from '@bluebeam/react-i18n';
import { ArgoxApp } from './ArgoxWebClientApp';
import ArgoxAppHeader from './ArgoxAppHeader';
import LoadingAnimation from '../components/LoadingAnimation';

type Routes = Record<string, string>;

export enum ArgoXFeatureModes {
  THREE_D_DRAWING = 'ThreeDDrawing',
  DRAWING_2_BIM = 'Drawing2BIM', // == CONNECTED_DRAWINGS
}

const legacyArgoXRoutes: Routes = {
  argox_list: '/labs/argox/project/list',
  argox_view: '/labs/argox/project/view',
  argox_sheets: '/labs/argox/project/sheets',
  argox_combineSheets: '/labs/argox/project/combine-sheets',
  argox_settings: '/labs/argox/project/settings',
  argox_dataConnection: '/labs/argox/project/data-connection',
  argox_edit: '/labs/argox/project/edit',
  argox_compare:
    '/labs/argox/project/:projectId/sheets/compare/sheet1/:sheet1/sheet2/:sheet2',
  argox_model: '/labs/argox/project/model',
  argox_create: '/labs/argox/project/create',
};

// TODO DEN-3395 remove deprecated Drawing2BIM code due to renaming it to ConnectedDrawings.
const threeDDrawingsRoutes: Routes = {
  threeDDrawings_list: '/labs/3d-drawings/project/list',
  threeDDrawings_sheets: '/labs/3d-drawings/project/sheets',
  threeDDrawings_connectSheets:
    '/labs/3d-drawings/project/sheets/connection/projectId/:projectId/imageId/:imageId',
  threeDDrawings_combineSheets: '/labs/3d-drawings/project/combine-sheets',
  threeDDrawings_manualCombineSheets:
    '/labs/3d-drawings/project/combine-sheets/projectId/:projectId/manual/sheetNumber/:sheetNumber/sheetName/:sheetName/drawingIds/*',
  threeDDrawings_compare:
    '/labs/3d-drawings/project/:projectId/sheets/compare/sheet1/:sheet1/sheet2/:sheet2',
  threeDDrawings_alignSheets:
    '/labs/3d-drawings/project/sheets/align/projectId/:projectId/sheet1/:sheet1/sheet2/:sheet2',
  threeDDrawings_create: '/labs/3d-drawings/project/create',
  threeDDrawings_threedDrawings:
    '/labs/3d-drawings/project/view/:projectId/threeDDrawings',
  threeDDrawings_settings: '/labs/3d-drawings/project/settings',
  threeDDrawings_companyTemplatePerSheet:
    '/labs/3d-drawings/project/settings/template/projectId/:projectId/companyName/:companyName',
  threeDDrawings_llm: '/labs/3d-drawings/project/llm/projectId/:projectId',
  threeDDrawings_edit: '/labs/3d-drawings/project/edit',
};

const connectedDrawingsRoutes: Routes = {
  connectedDrawings_list: '/labs/connected-drawings/project/list',
  connectedDrawings_view: '/labs/connected-drawings/project/view',
  connectedDrawings_sheets: '/labs/connected-drawings/project/sheets',
  connectedDrawings_settings: '/labs/connected-drawings/project/settings',
  connectedDrawings_dataConnection:
    '/labs/connected-drawings/project/data-connection/:id',
  connectedDrawings_edit: '/labs/connected-drawings/project/edit',
  connectedDrawings_model: '/labs/connected-drawings/project/model',
  connectedDrawings_editModel:
    '/labs/connected-drawings/project/model/edit/projectId/:projectId/modelId/:modelId',
  connectedDrawings_create: '/labs/connected-drawings/project/create',
  connectedDrawings_compareViews:
    '/labs/connected-drawings/project/compare/projectId/:projectId/view1/:viewId/view2/:viewId2',
  connectedDrawings_alignDrawing:
    '/labs/connected-drawings/project/align/:projectId/:modelId/:drawingId/:projectionId',
};

const getArgoXEnabledRoutes = (): string[] => {
  const { featureFlagService } = getAppServices();

  const isThreeDDrawingsEnabled = featureFlagService.hasFeature(
    FeatureFlag.ThreeDDrawings
  );

  const isConnectedDrawingsEnabled = featureFlagService.hasFeature(
    FeatureFlag.ConnectedDrawings
  );

  const enabledThreeDDrawingsRoutes = isThreeDDrawingsEnabled
    ? Object.values(threeDDrawingsRoutes)
    : [];
  const enableConnectedDrawingsRoutes = isConnectedDrawingsEnabled
    ? Object.values(connectedDrawingsRoutes)
    : [];

  return [
    ...Object.values(legacyArgoXRoutes),
    ...enabledThreeDDrawingsRoutes,
    ...enableConnectedDrawingsRoutes,
  ];
};

/**
 * A renderer that will display the Argox - Web Client contextually.
 */
const ArgoxWebClient: React.VFC = () => {
  const { translate } = useTranslator(['labsPage']);
  const { accessToken, idToken } = useAuth();

  const argoxWebRoutes = getArgoXEnabledRoutes();
  const supportedRouteMatch = useRouteMatch(argoxWebRoutes);

  const location = useLocation();

  if (supportedRouteMatch === null) {
    return null;
  }

  const mode = location.pathname.includes('3d-drawings')
    ? ArgoXFeatureModes.THREE_D_DRAWING
    : ArgoXFeatureModes.DRAWING_2_BIM;

  const headerTitle = location.pathname.includes('3d-drawings')
    ? translate('labsPage:pages.3dDrawings')
    : translate('labsPage:pages.connectedDrawings');

  const content = (
    <ErrorBoundary fallback={<LoadingAnimation loadingMessageKey="loading" />}>
      <React.Suspense
        fallback={<LoadingAnimation loadingMessageKey="loading" />}
      >
        <ArgoxAppHeader title={headerTitle} backURL="/labs" />
        <ArgoxApp
          credentials={{
            accessToken,
            idToken,
          }}
          mode={mode}
        />
      </React.Suspense>
    </ErrorBoundary>
  );

  return <div className="flex column h100 w100 grow-1">{content}</div>;
};

export default ArgoxWebClient;
